'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
export interface VisibilityIntervalOptions {
  /**
   * When true, fire the callback immediately on mount (if visible) and on
   * each hidden→visible transition, in addition to the regular interval ticks.
   */
  immediate?: boolean;
}

/**
 * Runs a callback on a repeating interval, but only when the document is visible.
 *
 * The callback is captured via a ref so changing `onTick` identity does **not**
 * restart the interval. Ticks that fire while `document.visibilityState` is not
 * `'visible'` are silently skipped.
 *
 * When `options.immediate` is true, the callback also fires immediately on mount
 * (if visible) and on each hidden→visible transition — useful for heartbeat-style
 * patterns where you want an immediate pulse plus periodic refreshes.
 */
export function useVisibilityInterval(onTick, intervalMs, enabled, options) {
  const $ = _c(7);
  const onTickRef = useRef(onTick);
  let t0;
  if ($[0] !== onTick) {
    t0 = () => {
      onTickRef.current = onTick;
    };
    $[0] = onTick;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(t0);
  const immediate = options?.immediate ?? false;
  let t1;
  let t2;
  if ($[2] !== enabled || $[3] !== immediate || $[4] !== intervalMs) {
    t1 = () => {
      if (!enabled || !Number.isFinite(intervalMs) || intervalMs <= 0) {
        return;
      }
      if (immediate && document.visibilityState === "visible") {
        onTickRef.current();
      }
      const id = globalThis.setInterval(() => {
        if (document.visibilityState === "visible") {
          onTickRef.current();
        }
      }, intervalMs);
      const handleVisibilityChange = function handleVisibilityChange() {
        if (document.visibilityState === "visible") {
          onTickRef.current();
        }
      };
      if (immediate) {
        document.addEventListener("visibilitychange", handleVisibilityChange);
      }
      return () => {
        globalThis.clearInterval(id);
        if (immediate) {
          document.removeEventListener("visibilitychange", handleVisibilityChange);
        }
      };
    };
    t2 = [enabled, intervalMs, immediate];
    $[2] = enabled;
    $[3] = immediate;
    $[4] = intervalMs;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
}